import {
  AdminTicketScans,
  CouponAssignmentWithPromotion,
  DecryptedTicketScan,
  CouponsApi,
  GetCouponImageRequest,
  GetCouponsForPlayerRequest,
  GetDecryptedTicketScanRequest,
  GetPlayerByLookupRequest,
  GetPlayerCapabilitiesRequest,
  GetRewardsForPlayerRequest,
  GetTicketScansForBarcodeRequest,
  GetTicketScansForPlayerRequest,
  PagePamPlayerSearchSummary,
  PlayerCapabilities,
  PlayerDetailsEditRequest,
  PlayerRewardsApi,
  PlayerRewardsPage,
  PlayerRewardStatus,
  PlayersApi,
  PlayerTicketScans,
  PlayerWithPAMDetails,
  SearchPlayersRequest,
  SpinHistoryAdminPage,
  SpinToWinApi,
  TicketScansApi,
  UpdatePlayerDetailsRequest,
} from '@Generated/back-office-client/src'
import { BackOfficeDataServiceAPI } from './back-office-data-service'

const getPlayerByLookup = async (playerLookupId: string): Promise<PlayerWithPAMDetails> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(PlayersApi)

  const request: GetPlayerByLookupRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    playerLookupId,
  }

  return await api.getPlayerByLookup(request)
}

const getPlayerTicketScans = async (
  playerLookupId: string,
  resultStart: number,
  resultEnd: number,
): Promise<PlayerTicketScans> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(TicketScansApi)

  const request: GetTicketScansForPlayerRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    playerLookupId,
    resultStart,
    resultEnd,
  }

  return await api.getTicketScansForPlayer(request)
}

const getFullTicketScan = async (scanId: string): Promise<DecryptedTicketScan> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(TicketScansApi)

  const request: GetDecryptedTicketScanRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    scanId,
  }

  return await api.getDecryptedTicketScan(request)
}

const getPlayerCoupons = async (
  playerLookupId: string,
): Promise<Array<CouponAssignmentWithPromotion>> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(CouponsApi)

  const request: GetCouponsForPlayerRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    playerLookupId,
  }

  return await api.getCouponsForPlayer(request)
}

const getCouponImage = async (promotionId: string, couponId: string): Promise<Blob> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(CouponsApi)

  const request: GetCouponImageRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    promotionId,
    couponId,
  }

  const imageResponse = await api.getCouponImageRaw(request)

  if (!imageResponse.raw.ok) {
    throw new Error('Error fetching image')
  }

  return await imageResponse.raw.blob()
}

const getPlayerSpinHistory = async (
  playerLookupId: string,
  resultStart: number,
  resultEnd: number,
): Promise<SpinHistoryAdminPage> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(SpinToWinApi)

  const request: GetTicketScansForPlayerRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    playerLookupId,
    resultStart,
    resultEnd,
  }

  return await api.getSpinHistoryForPlayer(request)
}

const getPlayerRewards = async (
  playerLookupId: string,
  resultStart: number,
  resultEnd: number,
  statuses?: PlayerRewardStatus[],
): Promise<PlayerRewardsPage> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(PlayerRewardsApi)

  const request: GetRewardsForPlayerRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    playerLookupId,
    resultStart,
    resultEnd,
    statuses,
  }

  return await api.getRewardsForPlayer(request)
}

const searchTicketScansByBarcode = async (barcode: string): Promise<AdminTicketScans> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(TicketScansApi)

  const request: GetTicketScansForBarcodeRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    ticketScanRequest: { barcode },
  }

  return await api.getTicketScansForBarcode(request)
}

const updatePlayerDetails = async (
  playerLookupId: string,
  request: PlayerDetailsEditRequest,
): Promise<PlayerWithPAMDetails> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(PlayersApi)
  const req: UpdatePlayerDetailsRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    playerLookupId,
    playerDetailsEditRequest: request,
  }
  return await api.updatePlayerDetails(req)
}

const getPlayerCapabilities = async (): Promise<PlayerCapabilities> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(PlayersApi)
  const request: GetPlayerCapabilitiesRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
  }
  return await api.getPlayerCapabilities(request)
}

const searchPlayers = async (
  search: string,
  resultStart: number,
  resultEnd: number,
): Promise<PagePamPlayerSearchSummary> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(PlayersApi)
  const request: SearchPlayersRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    search,
    resultStart,
    resultEnd,
  }
  return await api.searchPlayers(request)
}

export const PlayerServiceAPI = {
  getPlayerByLookup,
  getPlayerCapabilities,
  updatePlayerDetails,
  searchPlayers,
  getPlayerTicketScans,
  getFullTicketScan,
  getPlayerCoupons,
  getCouponImage,
  getPlayerSpinHistory,
  getPlayerRewards,
  searchTicketScansByBarcode,
}
